import * as z from 'zod';

/**
 * Moderation domain schemas — the admin review queue for the catalog entities
 * (restaurants, products, product categories, promotions).
 *
 * Shapes mirror `apps/cpanel/openapi-eats-admin.json` (1.00.455):
 * - the queue (`GET /admin/change-requests`) lists `v1ChangeRequest` rows
 *   flat, under the platform envelope's top-level `list`;
 * - the per-entity review card (`GET /admin/<plural>/{id}/moderations`) is a
 *   SLOTTED object under `data` — `{prod, manual, sync}` (+ the dish's
 *   `modifier_groups` for products). A slot with nothing pending is absent,
 *   never an empty shell;
 * - `payload`/`baseline`/`original_payload` are untyped Structs on purpose
 *   (the gate is entity-agnostic), so they stay `unknown` here.
 */

export const localizedTextSchema = z
  .object({
    ru: z.string().nullish(),
    uz: z.string().nullish(),
    en: z.string().nullish(),
  })
  .loose();
export type LocalizedText = z.infer<typeof localizedTextSchema>;

/**
 * Who acted on a change request (`v1ChangeRequestActor`): an admin person
 * (`kind: "admin"`, names may be empty until auth resolves them) or the
 * system (`kind: "system"`, e.g. `system:superseded`,
 * `system:moderation-disabled` — never has a name).
 */
export const changeRequestActorSchema = z
  .object({
    id: z.string().nullish(),
    kind: z.string().nullish(),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
  })
  .loose();
export type ChangeRequestActor = z.infer<typeof changeRequestActorSchema>;

/**
 * The entity one level above the request's own (`v1ChangeRequestParent`):
 * a dish's category, a modifier group's dish, a promotion's branch. Absent
 * for parentless types (restaurant, category) and network-wide promotions.
 */
export const changeRequestParentSchema = z
  .object({
    entity_type: z.string().nullish(),
    entity_id: z.string().nullish(),
    name: localizedTextSchema.nullish(),
  })
  .loose();
export type ChangeRequestParent = z.infer<typeof changeRequestParentSchema>;

/** `v1ChangeRequest` — one moderation request, exactly as the spec lists it. */
export const changeRequestSchema = z
  .object({
    id: z.string(),
    entity_type: z.string().nullish(),
    /** Absent for a pending create. */
    entity_id: z.string().nullish(),
    /** `"publish"` (a draft's first "ready") | `"update"` | `"delete"`. */
    operation: z.string().nullish(),
    merchant_id: z.string().nullish(),
    restaurant_id: z.string().nullish(),
    submitted_by: z.string().nullish(),
    payload: z.unknown().nullish(),
    payload_version: z.number().nullish(),
    /** Absent for create. */
    baseline: z.unknown().nullish(),
    baseline_updated_at: z.string().nullish(),
    /** `"pending" | "approved" | "rejected" | "stale" | "withdrawn"`. */
    status: z.string().nullish(),
    rejection_reason: z.string().nullish(),
    reviewed_at: z.string().nullish(),
    created_at: z.string().nullish(),
    updated_at: z.string().nullish(),
    /** `"manual"` (merchant panel) | `"sync"` (menu sync). */
    source: z.string().nullish(),
    entity_name: localizedTextSchema.nullish(),
    /** The merchant's own payload — only on a request amended before approval. */
    original_payload: z.unknown().nullish(),
    amended_at: z.string().nullish(),
    reviewer: changeRequestActorSchema.nullish(),
    amender: changeRequestActorSchema.nullish(),
    parent: changeRequestParentSchema.nullish(),
  })
  .loose();
export type ChangeRequest = z.infer<typeof changeRequestSchema>;

/**
 * One version's entity — each queue returns its own localized message
 * (`v1LocalizedProduct`, `v1LocalizedRestaurant`, …). Only the fields every
 * type shares are typed; the rest stay on the loose bag for the readers.
 */
export const moderationEntitySchema = z
  .object({
    id: z.string().nullish(),
    name: localizedTextSchema.nullish(),
    merchant_id: z.string().nullish(),
    restaurant_id: z.string().nullish(),
    updated_at: z.string().nullish(),
    lifecycle_status: z.string().nullish(),
  })
  .loose();
export type ModerationEntity = z.infer<typeof moderationEntitySchema>;

/** One queue row — the unified admin queue lists change requests directly. */
export type ModerationRow = ChangeRequest;

/**
 * One branch's effective dish row (`v1RestaurantProduct`) — `price` is what
 * the branch charges (its own override or the inherited product price),
 * `own_price` only the override.
 */
export const branchPriceSchema = z
  .object({
    restaurant_id: z.string().nullish(),
    product_id: z.string().nullish(),
    price: z.number().nullish(),
    own_price: z.number().nullish(),
    stock_quantity: z.number().nullish(),
    status: z.string().nullish(),
  })
  .loose();
export type BranchPrice = z.infer<typeof branchPriceSchema>;

/** One add-on's per-branch count + switch. Absent rows read as on/uncounted. */
export const modifierOptionStateSchema = z
  .object({
    restaurant_id: z.string().nullish(),
    option_id: z.string().nullish(),
    stock_quantity: z.number().nullish(),
    status: z.string().nullish(),
  })
  .loose();
export type ModifierOptionState = z.infer<typeof modifierOptionStateSchema>;

/** Modifier add-on on the product entity (`modifiers[].options[]`). */
export const modifierOptionSchema = z
  .object({
    id: z.string().nullish(),
    name: localizedTextSchema.nullish(),
    price: z.number().nullish(),
    status: z.string().nullish(),
    is_unavailable: z.boolean().nullish(),
    min_amount: z.number().nullish(),
    max_amount: z.number().nullish(),
    default_selected: z.boolean().nullish(),
    sort_order: z.number().nullish(),
    /** Fiscal unit code (`1` kg, `2` g, `3` l, …). */
    unit_code: z.string().nullish(),
  })
  .loose();
export type ModifierOption = z.infer<typeof modifierOptionSchema>;

export const modifierGroupSchema = z
  .object({
    id: z.string().nullish(),
    name: localizedTextSchema.nullish(),
    min_select: z.number().nullish(),
    max_select: z.number().nullish(),
    sort_order: z.number().nullish(),
    options: z.array(modifierOptionSchema).default([]),
  })
  .loose();
export type ModifierGroup = z.infer<typeof modifierGroupSchema>;

/** Restaurant hours are wall-clock times, including close_time "24:00". */
export const weekScheduleSchema = z.array(
  z
    .object({
      day_of_week: z.string().optional(),
      /** Change-request payloads and baselines use `day`. */
      day: z.string().optional(),
      open_time: z.string().optional(),
      close_time: z.string().optional(),
      order_from: z.string().optional(),
      order_to: z.string().optional(),
      is_closed: z.boolean().optional(),
    })
    .loose(),
);

/** The three closed slots of a review card. */
export const MODERATION_SLOTS = ['prod', 'manual', 'sync'] as const;
export type ModerationSlot = (typeof MODERATION_SLOTS)[number];
/** The two slots that carry a pending request (one per source). */
export type ModerationPendingSlot = Exclude<ModerationSlot, 'prod'>;

/**
 * One version of the entity (`v1<Type>ModerationVersion`): in `prod` the live
 * row with no request attached, in `manual`/`sync` the live row with that
 * slot's pending patch already applied — a full entity, never a diff.
 * `kind` is not on the wire: the normalizer stamps it from the slot name so
 * slot-agnostic helpers (branch-price / option-state grids) can tell the
 * live version from the pending ones.
 */
export const moderationVersionSchema = z
  .object({
    kind: z.enum(MODERATION_SLOTS).optional(),
    entity: moderationEntitySchema.nullish(),
    change_request: changeRequestSchema.nullish(),
    /** Restaurants only: the full week, beside entity (not working_hours). */
    week_schedule: weekScheduleSchema.nullish(),
    /** Products only: every branch row the request touches. */
    restaurant_products: z.array(branchPriceSchema).default([]),
    /** Products only: per-branch add-on switches the request touches. */
    modifier_option_states: z.array(modifierOptionStateSchema).default([]),
    /** Fields the slot's patch changes against `prod` (server-computed). */
    changed_fields: z.array(z.string()).default([]),
  })
  .loose();
export type ModerationVersion = z.infer<typeof moderationVersionSchema>;

/** A dish's modifier group with a request of its OWN waiting. */
export const modifierGroupCardSchema = z
  .object({
    prod: moderationVersionSchema.nullish(),
    manual: moderationVersionSchema.nullish(),
    sync: moderationVersionSchema.nullish(),
  })
  .loose();
export type ModifierGroupCard = z.infer<typeof modifierGroupCardSchema>;

/**
 * `v1<Type>ModerationCard` — what one entity looks like to a moderator: the
 * live row and the at most two requests waiting on it, one per source.
 */
export const moderationCardSchema = z
  .object({
    prod: moderationVersionSchema.nullish(),
    manual: moderationVersionSchema.nullish(),
    sync: moderationVersionSchema.nullish(),
    /** Products only (`v1ProductModerationCard.modifier_groups`). */
    modifier_groups: z.array(modifierGroupCardSchema).default([]),
  })
  .loose();
export type ModerationCard = z.infer<typeof moderationCardSchema>;
